import React from 'react';
import { Link } from 'react-router-dom';
import { Truck, Package, Clock, DollarSign, CheckCircle, MapPin, Mail } from 'lucide-react';

const ShippingInfo = () => {

  const whiteGloveServices = [
    'Professional delivery team',
    'Furniture placement in room of choice',
    'Packaging removal and disposal',
    'Assembly service (if required)',
    'Inspection before delivery completion',
  ];

  return (
    <div className="min-h-screen bg-dark-50">
      
      {/* Breadcrumbs */}
      <div className="bg-white border-b border-dark-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center gap-2 text-sm font-body">
            <Link to="/" className="text-primary-600 hover:text-primary-700">Home</Link>
            <span className="text-dark-400">/</span>
            <span className="text-dark-700 font-semibold">Shipping Information</span>
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <div className="bg-gradient-primary text-white py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center animate-fade-in">
            <Truck className="w-16 h-16 mx-auto mb-6 opacity-90" />
            <h1 className="text-4xl text-dark-900 md:text-5xl lg:text-6xl font-display font-bold mb-6">
              Shipping & Delivery Information
            </h1>
            <p className="text-xl md:text-2xl text-dark-700 max-w-3xl mx-auto font-body">
              Professional white glove delivery service across Ontario
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
        
        {/* Delivery Overview */}
        <div className="bg-white rounded-2xl shadow-soft p-8 md:p-12 mb-12 animate-slide-up">
          <h2 className="text-3xl font-display font-bold text-dark-900 mb-6 text-center">
            How We Deliver Your Furniture
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Package className="w-8 h-8 text-primary-600" />
              </div>
              <div className="w-8 h-8 bg-primary-600 text-white rounded-full flex items-center justify-center mx-auto mb-2 font-bold">
                1
              </div>
              <h3 className="font-display font-bold text-dark-900 mb-2">Order Placed</h3>
              <p className="text-sm text-dark-600 font-body">
                Your order is confirmed and prepared
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-accent-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Clock className="w-8 h-8 text-accent-600" />
              </div>
              <div className="w-8 h-8 bg-accent-600 text-white rounded-full flex items-center justify-center mx-auto mb-2 font-bold">
                2
              </div>
              <h3 className="font-display font-bold text-dark-900 mb-2">Processing</h3>
              <p className="text-sm text-dark-600 font-body">
                Quality check and packaging (1-2 days)
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Truck className="w-8 h-8 text-primary-600" />
              </div>
              <div className="w-8 h-8 bg-primary-600 text-white rounded-full flex items-center justify-center mx-auto mb-2 font-bold">
                3
              </div>
              <h3 className="font-display font-bold text-dark-900 mb-2">In Transit</h3>
              <p className="text-sm text-dark-600 font-body">
                On the way to your address
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-success" />
              </div>
              <div className="w-8 h-8 bg-success text-white rounded-full flex items-center justify-center mx-auto mb-2 font-bold">
                4
              </div>
              <h3 className="font-display font-bold text-dark-900 mb-2">Delivered</h3>
              <p className="text-sm text-dark-600 font-body">
                Set up in your home (3-5 days)
              </p>
            </div>
          </div>

          <div className="bg-accent-50 border border-accent-200 rounded-xl p-6 text-center">
            <p className="text-lg font-body text-dark-900">
              <span className="font-bold">Standard Delivery Time:</span> 3-5 business days from order confirmation
            </p>
          </div>
        </div>

        {/* White Glove Service */}
        <div className="bg-gradient-accent text-white rounded-2xl shadow-colored p-8 md:p-12 mb-12">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-10">
              <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10" />
              </div>
              <h2 className="text-3xl font-display font-bold mb-4">
                White Glove Delivery Included
              </h2>
              <p className="text-xl text-accent-100 font-body">
                Premium service at no extra cost - because your furniture deserves the best care
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {whiteGloveServices.map((service, index) => (
                <div 
                  key={index}
                  className="flex items-start gap-3 bg-white/10 rounded-lg p-4 backdrop-blur-sm"
                >
                  <CheckCircle className="w-6 h-6 flex-shrink-0 mt-0.5" />
                  <span className="font-body">{service}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Payment Options */}
        <div className="bg-white rounded-2xl shadow-soft p-8 md:p-12 mb-12">
          <h2 className="text-3xl font-display font-bold text-dark-900 mb-6 text-center">
            Payment Method
          </h2>
          
          <div className="max-w-2xl mx-auto">
            <div className="bg-gradient-primary text-dark-700 rounded-xl p-8 text-center">
              <DollarSign className="w-16 h-16 mx-auto mb-4" />
              <h3 className="text-2xl font-display font-bold mb-3">
                Cash on Delivery (COD)
              </h3>
              <p className="text-lg text-dark-700 font-body mb-6">
                Pay when your furniture arrives - safe, secure, and convenient
              </p>
              <div className="space-y-2 text-left">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5" />
                  <span className="font-body">Inspect before you pay</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5" />
                  <span className="font-body">No online payment required</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5" />
                  <span className="font-body">Cash or card accepted on delivery</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Important Notes */}
        <div className="bg-white rounded-2xl shadow-soft p-8 md:p-12 mb-12">
          <h2 className="text-3xl font-display font-bold text-dark-900 mb-8 text-center">
            Important Delivery Notes
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            <div className="p-6 bg-dark-50 rounded-xl border-l-4 border-primary-500">
              <h3 className="font-display font-bold text-dark-900 mb-2">📞 Delivery Contact</h3>
              <p className="text-dark-600 font-body text-sm">
                Our team will call you 24 hours before delivery to confirm the time window
              </p>
            </div>

            <div className="p-6 bg-dark-50 rounded-xl border-l-4 border-accent-500">
              <h3 className="font-display font-bold text-dark-900 mb-2">🏠 Access Required</h3>
              <p className="text-dark-600 font-body text-sm">
                Please ensure clear access to your home and the room where furniture will be placed
              </p>
            </div>

            <div className="p-6 bg-dark-50 rounded-xl border-l-4 border-primary-500">
              <h3 className="font-display font-bold text-dark-900 mb-2">📏 Measure Doorways</h3>
              <p className="text-dark-600 font-body text-sm">
                Check that doorways, stairwells, and elevators can accommodate your furniture
              </p>
            </div>

            <div className="p-6 bg-dark-50 rounded-xl border-l-4 border-accent-500">
              <h3 className="font-display font-bold text-dark-900 mb-2">👤 Someone Home</h3>
              <p className="text-dark-600 font-body text-sm">
                An adult (18+) must be present to receive and sign for the delivery
              </p>
            </div>
          </div>
        </div>

        {/* FAQ Quick Links */}
        <div className="bg-primary-50 border border-primary-200 rounded-2xl p-8">
          <h3 className="text-2xl font-display font-bold text-dark-900 mb-4 text-center">
            Have More Questions?
          </h3>
          <p className="text-center text-dark-600 font-body mb-6">
            Check our FAQ page or contact our customer service team
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/faq"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-white border border-primary-300 text-primary-700 rounded-xl font-body font-semibold hover:bg-primary-50 transition-smooth"
            >
              View FAQ
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-white border border-primary-300 text-primary-700 rounded-xl font-body font-semibold hover:shadow-colored transition-smooth"
            >
              <Mail className="w-5 h-5" />
              Contact Us
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShippingInfo;